import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { TimeOffType } from '@app/models/time-off-v3/time-off-type.model';
import { BulkTimeOffImporterName } from '@app/modules/reporting/enums';
import { BulkImportMeta } from '@app/modules/reporting/interfaces/bulk-import-meta-data.interface';
import { TimeOffService } from '@app/modules/time-off-v3/services';
import { AnalyticService, FileHelperService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { BULK_IMPORT_META_DATA } from '../bulk-import-config';
@Component({
    templateUrl: './bulk-import-time-off.view.html',
    styleUrls: ['./bulk-import-time-off.style.scss'],
})
export class BulkImportTimeOff {
    isLoading = false;
    isProcessing = false;
    savedFileId: number;
    importer: BulkImportMeta;
    importMetaData: BulkImportMeta[] = [];
    timeOffTypes: TimeOffType[];
    selectedTimeOffTypeId: number;
    saved = false;
    showPayrollMessage = false;

    constructor(
        private activatedRoute: ActivatedRoute,
        private notify: NotifyService,
        private timeOffService: TimeOffService,
        private featureService: FeatureService,
        private fileHelper: FileHelperService,
        private analyticService: AnalyticService
    ) {}

    async ngOnInit(): Promise<void> {
        await this.featureFlagFilterMetaData();
        this.setImporterMetaFromRoute();
        this.getTimeOffTypes();
    }

    async featureFlagFilterMetaData(): Promise<void> {
        for (const importMeta of BULK_IMPORT_META_DATA) {
            if (importMeta.showIfHasFeatureFlag && !(await this.featureService.has(importMeta.showIfHasFeatureFlag))) {
                continue;
            }

            if (importMeta.hideIfHasFeatureFlag && (await this.featureService.has(importMeta.hideIfHasFeatureFlag))) {
                continue;
            }

            this.importMetaData.push(importMeta);
        }
    }

    getImportTemplate(): void {
        if (!this.selectedTimeOffTypeId) {
            return;
        }

        this.timeOffService.getDefaultImportTemplate(this.selectedTimeOffTypeId, this.importer.importEndpoint);
    }

    onTimeOffTypeChange(): void {
        const selectedTimeOffType = this.timeOffTypes.find(
            (timeOffType) => timeOffType.id === this.selectedTimeOffTypeId
        );

        this.showPayrollMessage = !!selectedTimeOffType.incomeTypeId;
    }

    async getTimeOffTypes(): Promise<void> {
        this.isLoading = true;
        const [types] = await TimeOffType.all();
        this.timeOffTypes = types;
        this.selectedTimeOffTypeId = types.length > 0 ? types[0].id : null;
        this.showPayrollMessage = !!types[0]?.incomeTypeId;
        this.isLoading = false;
    }

    async importFileSelected(file: File): Promise<void> {
        if (!file) {
            return;
        }

        this.isLoading = true;
        try {
            const savedFile = await this.timeOffService.importFile(
                file,
                this.importer.importEndpoint,
                this.importer.importerName
            );
            // Use this file id for later to start the import process
            this.savedFileId = savedFile.id;
            this.isLoading = false;
        } catch (err) {
            if (err.error) {
                this.notify.error(err.error.errors.map((err) => err.detail).join('\n'));
                this.isLoading = false;
                return;
            }

            this.notify.error('reporting.data-import.fileCouldNotBeValidated');
            this.isLoading = false;
        }
    }

    async import(): Promise<void> {
        this.isLoading = true;
        try {
            await this.timeOffService.import(this.savedFileId, this.selectedTimeOffTypeId, this.importer.importerName);
            this.isLoading = false;
            this.savedFileId = null;
            this.saved = true;
            if (this.importer.importerName === BulkTimeOffImporterName.Requests) {
                this.analyticService.trackEvent(AnalyticEvents.ReportingDataImportTimeOffRequestsImport);
            }
            if (this.importer.importerName === BulkTimeOffImporterName.Balances) {
                this.analyticService.trackEvent(AnalyticEvents.ReportingDataImportTimeOffBalancesImport);
            }
            if (this.importer.importerName === BulkTimeOffImporterName.Adjustments) {
                this.analyticService.trackEvent(AnalyticEvents.ReportingDataImportTimeOffAdjustmentsImport);
            }
        } catch (err) {
            this.notify.error('reporting.data-import.importWasUnsuccessful');
            this.fileHelper.save(err.error.attributes.file_id, this.importer.importerName + '-error-file', 'csv');
            this.isLoading = false;
            this.savedFileId = null;
        }
    }

    private setImporterMetaFromRoute(): void {
        this.importer = this.importMetaData.find((entity) => {
            return entity.routerLink.includes(this.activatedRoute.snapshot.params.entity);
        });

        if (!this.importer) {
            this.notify.error('reporting.data-import.couldNotFindTheImportTemplate');
            return;
        }
    }
}
